"use client";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageCompetitionSchema } from "@/sanity/pages/PageCompetitionTemplate";
import { CTAButton } from "@/shadcn/ui/button";
import ChipSerif from "@/components/global/ui/ChipSerif";
import Image from "next/image";
import arrowsDown from "@/public/global/svg/arrowsDown.svg";
import moonTexture from "@/public/competition/moonTexture.png";
import bgBlue from "@/public/competition/bgBlue.png";


export default function TableSection({
  data,
  className,
  isBrazil,
}: {
  data: PageCompetitionSchema;
  className?: string;
  isBrazil?: boolean;
}) {
  return (
    <section className={cn("w-full px-[2rem] lg:px-[3rem] relative", className)}>
      <div className="relative flex flex-col gap-[5rem] lg:gap-[25.37rem] items-center pt-[6.25rem] lg:pt-[15.25rem] pb-[7.63rem] lg:pb-[24.25rem] rounded-[1.5rem] lg:rounded-[5rem] bg-[#103037] overflow-hidden">
        <div className="absolute bottom-[88rem] left-0 w-full h-[50rem] z-[1] bg-gradient-to-b from-[#103037] to-[transparent]"></div>
        <Image src={bgBlue} alt="" className="absolute bottom-0 left-0 w-full h-auto mix-blend-luminosity z-0" quality={100} />

        <div className="relative container flex flex-col items-center z-10 px-[3rem] lg:px-0 gap-[4rem] lg:gap-[9rem]">
          <div className="w-full flex flex-col gap-[1.5rem] items-center justify-center">
            {data?.realFundindgChip && (
              <ChipSerif text={data.realFundindgChip} className="text-white mb-0" />
            )}

            {data?.realFundindgText && (
              <div className="text-white text-center w-[min(34rem,100%)] lg:w-[min(78.5rem,100%)] prose-h2:mb-0 prose-p:text-[1.75rem] prose-p:max-w-[47.5rem]">
                <PortableText
                  value={data.realFundindgText}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          <div className="w-[min(84rem,100%)] relative flex flex-col items-center">
            <div className="hidden md:block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[110%] lg:w-[91.75rem] h-[calc(100%-14rem)] bg-[#FFF7E2]/[55%] rounded-[1.25rem] lg:rounded-[2.25rem] z-0" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[98%] h-[calc(100%-3rem)] lg:h-[calc(100%-7rem)] bg-[#FFF7E2]/50 backdrop-blur-[4rem] rounded-[1.25rem] lg:rounded-[2.25rem] z-[1]" />

            <div className="absolute w-[7.375rem] aspect-square rounded-[1.394rem] lg:flex hidden items-center justify-center top-[-3.75rem] left-1/2 -translate-x-1/2 bg-[#E0D0A6] z-[3]">
              <svg className="w-[3.29613rem] h-[3.19275rem]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 26" fill="none">
                <path d="M26.369 19.5053C22.6383 19.5053 19.6022 16.4839 19.6022 12.7712H13.5361C13.5361 19.8131 19.2929 25.542 26.369 25.542V19.5053Z" fill="#103037"/>
                <path d="M12.8321 0C5.75597 0 0 5.72815 0 12.7701C0 19.812 5.75597 25.5409 12.8321 25.5409V19.5041C9.10142 19.5041 6.0661 16.4834 6.0661 12.7701C6.0661 9.05668 9.10142 6.03678 12.8321 6.03678C16.5627 6.03678 19.5988 9.05744 19.5988 12.7701H25.6649C25.6649 5.72815 19.9082 0 12.8321 0Z" fill="#103037"/>
              </svg>
            </div>

            <div className="relative !w-[min(72.37663rem,100%)] flex flex-col gap-[2.63rem] items-center justify-center bg-[#FFF7E2] rounded-[2.25rem] pt-[3rem] pb-[4.87rem] lg:pt-[6.5rem] lg:pb-[5.63rem] overflow-hidden z-[2]">
              <Image src={moonTexture} alt="" className="absolute top-0 left-0 w-full h-full z-0 mix-blend-luminosity" />

              {data?.realFundindgArrowText && (
                <span className="text-[#103037] text-[2rem] lg:text-[2.25rem] lg:leading-none tracking-[-0.0675rem] z-10">{data.realFundindgArrowText}</span>
              )}

              <Image src={arrowsDown} alt="" className="w-[34.375rem] z-10" />

              <div className="flex flex-row items-end gap-[1rem] z-10">
                <div className="border border-[#103037]/20 rounded-full py-[1rem] px-[1.12rem] my-auto">
                  <svg className="w-[2.99238rem] h-[3.08888rem]"xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 25" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M14.4046 13.2833C14.2686 13.2947 13.5658 13.3417 11.9982 13.3417C10.7513 13.3417 9.86605 13.2998 9.55547 13.2833C4.73693 13.0458 1.14031 12.1058 1.14031 10.9803C1.14031 9.85483 4.73693 8.91609 9.55547 8.67474V12.3471C9.87058 12.3725 10.7729 12.4322 12.0197 12.4322C13.5159 12.4322 14.2652 12.3624 14.4001 12.3484V8.67728C19.2084 8.91736 22.7971 9.85737 22.7971 10.9803C22.7971 12.1032 19.2095 13.0432 14.4001 13.282L14.4046 13.2833ZM14.4046 8.29747V5.01125H21.115V0H2.8451V5.01125H9.55433V8.2962C4.10103 8.57693 0 9.78751 0 11.2382C0 12.6888 4.10103 13.8981 9.55433 14.1801V24.7108H14.4035V14.1763C19.8443 13.8956 23.9385 12.6863 23.9385 11.2369C23.9385 9.78751 19.8477 8.5782 14.4035 8.2962L14.4046 8.29747Z" fill="#103037"/>
                  </svg>
                </div>
                <h2 className="text-[5.25rem] lg:text-[6.75rem] font-[500] tracking-normal !text-[#103037] lg:leading-[90%] mb-0">{data.realFundindgBigNumber}</h2>
                <span className="text-[1.09075rem] text-[#103037] font-[700] uppercase mb-[1rem]">USDT</span>
              </div>

              <div className="richtext w-full prose-p:text-[1.5rem] prose-p:tracking-normal prose-p:text-[#103037] prose-p:uppercase prose-p:mb-0 z-10">
                {data?.realFundindgSmallText && (
                  <PortableText
                    value={data.realFundindgSmallText}
                    components={richTextComponent}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center w-full lg:w-[min(146.75rem,100%)] gap-[4rem] lg:gap-[4.88rem] relative z-10">
          <div className={cn(
            "flex flex-col items-center text-center gap-[1.5rem] prose-h2:text-white prose-h2:mb-0 px-[3rem] lg:px-0",
            isBrazil ? "max-w-[105rem]" : "max-w-[81.5rem]"
          )}>
            <ChipSerif text={data?.tableChip} className="text-white mb-0" />

            {data?.tableTitle && (
              <PortableText
                value={data.tableTitle}
                components={richTextComponent}
              />
            )}
          </div>

          <div
            className="flex flex-col w-full max-w-[146.75rem] overflow-x-auto [&::-webkit-scrollbar]:h-[0.8rem] [&::-webkit-scrollbar-track]:bg-[#E6EDE9] [&::-webkit-scrollbar-track]:rounded-b-[1.5rem] [&::-webkit-scrollbar-thumb]:bg-[#ffffff30] [&::-webkit-scrollbar-thumb]:rounded-b-[1.5rem]"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#ffffff30 #E6EDE9',
            } as React.CSSProperties}
          >
              {/* head */}
              <div className="flex w-full lg:mt-[1.5rem]">
                <div className="w-[3rem] lg:w-0 shrink-0" />
                <div className="flex items-center bg-white/20 border border-white/10 rounded-tl-[1.5rem] min-h-[10rem] px-[1.75rem] lg:px-[2.5rem] lg:h-[10rem] w-full min-w-[17.85rem]">
                <span className="font-sf text-white/90 text-[2rem] lg:text-[2.25rem] font-[300] leading-[150%]">
                  {data?.tableColumnTitles?.firstColumn}
                </span>
              </div>
              <div className="flex items-center bg-white/20 border border-white/10 min-h-[10rem] px-[1.75rem] lg:h-[10rem] w-full min-w-[17.85rem] lg:px-[2.5rem]">
                <span className="font-sf text-white/90 text-[2rem] lg:text-[2.25rem] font-[300] leading-[150%]">
                  {data?.tableColumnTitles?.secondColumn}
                </span>
              </div>
              <div className="flex items-center bg-white/20 border border-white/10 rounded-tr-[1.5rem] min-h-[10rem] px-[1.75rem] lg:px-[2.5rem] lg:h-[10rem] w-full min-w-[37rem]">
                <span className="font-sf text-white/90 text-[2rem] lg:text-[2.25rem] font-[300] leading-[150%]">
                  {data?.tableColumnTitles?.thirdColumn}
                </span>
              </div>
              <div className="w-[3rem] lg:w-0 shrink-0" />
            </div>
            {/* head */}
            {/* body */}
            {data?.tablePlaces?.map((place, index) => (
              <div key={index} className="flex w-full">
                <div className="w-[3rem] lg:w-0 shrink-0" />
                <div
                  className={cn(
                    " bg-white/[7%] border border-white/10 backdrop-blur-lg h-[12rem] lg:h-[10rem] min-h-[10rem] w-full min-w-[17.85rem] flex items-center px-[1.75rem] lg:px-[2.5rem]",
                    index + 1 === data?.tablePlaces?.length &&
                      "rounded-bl-[1.5rem]",
                  )}
                >
                  <span className="font-sf text-white text-[2rem] lg:text-[3rem] font-[400] leading-[150%]">
                    {place?.position}
                  </span>
                </div>
                <div className="flex items-center bg-white/[7%] border border-white/10 backdrop-blur-lg h-[12rem] min-h-[10rem] w-full min-w-[17.85rem] px-[1.75rem] lg:h-[10rem] lg:px-[2.5rem]">
                  <span className="font-sf text-white/90 text-[1.75rem] lg:text-[2.25rem] font-[300] leading-[150%] ">
                    {place?.prize}
                  </span>
                </div>
                <div
                  className={cn(
                    "bg-white/[7%] border border-white/10 backdrop-blur-lg lg:h-[10rem] h-[12rem] min-h-[10rem] w-full min-w-[37rem] flex items-center px-[1.75rem] lg:px-[2.5rem]",
                    index + 1 === data?.tablePlaces?.length &&
                      "rounded-br-[1.5rem]",
                  )}
                >
                  <span className="font-sf text-white/90 text-[1.75rem] lg:text-[2.25rem] font-[300] leading-[150%] ">
                    {place?.challengeAndFees}
                  </span>
                </div>
                <div className="w-[3rem] lg:w-0 shrink-0" />
              </div>
            ))}
            {/* body */}
          </div>

          {data?.tableCta && (
            <CTAButton data={data.tableCta} className="mt-8" />
          )}
        </div>
      </div>
    </section>
  );
}
